import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary that catches ChunkLoadErrors and other runtime errors.
 * When a ChunkLoadError occurs (e.g., after a deployment), it will automatically
 * reload the page once to fetch the latest code.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  private static REFRESH_KEY = 'chunk_load_retry_attempted';

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by ChunkErrorBoundary:', error, errorInfo);

    // Check if this is a ChunkLoadError
    const isChunkLoadError =
      error.name === 'ChunkLoadError' ||
      /Loading chunk [\d]+ failed/.test(error.message) ||
      /Failed to fetch dynamically imported module/.test(error.message);

    if (isChunkLoadError) {
      const hasRefreshed = sessionStorage.getItem(ChunkErrorBoundary.REFRESH_KEY);

      if (!hasRefreshed) {
        // Set the flag and reload
        sessionStorage.setItem(ChunkErrorBoundary.REFRESH_KEY, 'true');
        window.location.reload();
      } else {
        // Already refreshed once, clear the flag for next time
        sessionStorage.removeItem(ChunkErrorBoundary.REFRESH_KEY);
      }
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Check if it's a chunk load error
      const isChunkLoadError =
        this.state.error?.name === 'ChunkLoadError' ||
        /Loading chunk [\d]+ failed/.test(this.state.error?.message || '') ||
        /Failed to fetch dynamically imported module/.test(this.state.error?.message || '');

      if (isChunkLoadError) {
        // Show a brief loading message while reloading
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '18px',
                marginBottom: '12px',
                color: '#374151',
              }}>
                Updating application...
              </div>
              <div style={{
                fontSize: '14px',
                color: '#6B7280',
              }}>
                Please wait while we load the latest version
              </div>
            </div>
          </div>
        );
      }

      // For other errors, show a generic error message
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: '20px',
        }}>
          <div style={{ textAlign: 'center', maxWidth: '500px' }}>
            <div style={{
              fontSize: '24px',
              marginBottom: '16px',
              color: '#EF4444',
              fontWeight: 'bold',
            }}>
              Something went wrong
            </div>
            <div style={{
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '24px',
            }}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '500',
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
